import { Canvas } from 'fabric/fabric-impl';
import { useCallback, useEffect } from 'react';

import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useDrawingOperations } from './useDrawingOperations';

import { CANVAS_OBJECT_TYPE_PREFIX, DEFAULT_ZOOM } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { isCanvasSafe } from '~/core/utils/canvasSafety';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectCurrentRoute } from '~/redux/slices/route';
import { RouteEnum } from '~/redux/slices/route/route.types';
import {
  selectSelectedDrawingObjectName,
  updateSelectedDrawingObjectName,
} from '~/redux/slices/shape';

export const useDrawFabricCanvas = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const selectedColor = useAppSelector(selectSelectedColor);
  const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);
  const currentRoute = useAppSelector(selectCurrentRoute);
  const selectedDrawingObjectName = useAppSelector(
    selectSelectedDrawingObjectName
  );

  const {
    getters: {
      areThereShapesInTheDrawingArea,
      drawingShapes,
      activeDrawingObject,
    },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const {
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
  } = useDrawingOperations();

  const shouldDrawShapes = useCallback(
    () =>
      currentRoute === RouteEnum.DESIGN &&
      needRedrawCanvas &&
      areThereShapesInTheDrawingArea &&
      !!drawingShapes &&
      selectedColor?.overlayColor,
    [
      currentRoute,
      needRedrawCanvas,
      areThereShapesInTheDrawingArea,
      drawingShapes,
      selectedColor,
    ]
  );

  const validateCanvasSafety = useCallback(() => {
    if (!isCanvasSafe(canvas)) {
      console.warn('Canvas not safe for drawing, skipping draw operation');

      return false;
    }

    return true;
  }, [canvas]);

  const ensureCanvasZoom = useCallback(() => {
    try {
      const currentZoom = canvas.getZoom();

      if (!currentZoom || currentZoom === 0 || !isFinite(currentZoom)) {
        canvas.setZoom(DEFAULT_ZOOM);
      }
    } catch (error) {
      console.warn('Error checking/setting zoom:', error);

      return false;
    }

    return true;
  }, [canvas]);

  const performDrawing = useCallback(() => {
    try {
      if (!clearAndUpdateShapes(canvas) || !selectedColor?.overlayColor) {
        return () => undefined;
      }

      const { renameSubscriptions } = updateTextsAndArea(
        selectedColor.overlayColor
      );

      if (!fillCanvasAndRender(canvas, selectedColor, drawingShapes)) {
        return () => undefined;
      }

      if (activeDrawingObject?.object?.type === 'shape') {
        const activeDrawingShape = drawingShapes.find(
          ({ id }) => id === activeDrawingObject?.object?.id
        );

        if (
          activeDrawingShape?.label &&
          activeDrawingShape?.label !== selectedDrawingObjectName
        ) {
          dispatch(updateSelectedDrawingObjectName(activeDrawingShape.label));
        }
      }

      safeRenderCanvas(canvas);
      canvas.clone((canvasClone: Canvas) => {
        drawingTool.removeObjectByName(
          canvasClone,
          CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
        );
      });
      updateDrawingShapes([...drawingShapes]);
      finalizeDrawing();

      return () => {
        renameSubscriptions?.forEach((subscription) =>
          subscription?.unsubscribe()
        );
      };
    } catch (error) {
      console.error('Error drawing shapes on canvas:', error);

      return () => undefined;
    }
  }, [
    dispatch,
    canvas,
    selectedColor,
    drawingShapes,
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
  ]);

  const drawShapes = useCallback(() => {
    if (!shouldDrawShapes()) {
      return () => undefined;
    }

    if (!validateCanvasSafety()) {
      return () => undefined;
    }

    if (!ensureCanvasZoom()) {
      return () => undefined;
    }

    return performDrawing();
  }, [
    shouldDrawShapes,
    validateCanvasSafety,
    ensureCanvasZoom,
    performDrawing,
  ]);

  useEffect(() => drawShapes(), [drawShapes]);

  return {
    drawShapes,
  };
};
